import { Component, DestroyRef, Input, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { USAGE_CHART } from '../usage-report/config/usage-report.config';
import {
  UsageAggregationFilter,
  UsageAggregationFrequencyDateFormatMap,
  UsageAggregationPage,
} from '@markant-platform/usage/data-access';
import { catchError, combineLatest, EMPTY, Observable, of, ReplaySubject, Subject, switchMap } from 'rxjs';
import { ChartConfiguration } from 'chart.js';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { BaseChartDirective } from 'ng2-charts';
import { DateFormat } from '@markant-platform/shared/util-helpers';
import { SnackbarService } from '@markant-platform/shared/snackbar/feature';

@Component({
  selector: 'markant-platform-usage-report-chart',
  standalone: true,
  imports: [CommonModule, BaseChartDirective, TranslocoModule],
  templateUrl: './usage-report-chart.component.html',
  styleUrls: ['./usage-report-chart.component.scss'],
})
export class UsageReportChartComponent implements OnInit {
  @ViewChild(BaseChartDirective, { static: true }) baseChart!: BaseChartDirective;

  @Input() set filter(filter: UsageAggregationFilter | undefined) {
    if (filter) {
      this.filterChanged$.next(filter);
    }
  }

  @Input({ required: true }) loadFn!: (filter: UsageAggregationFilter) => Observable<UsageAggregationPage>;

  protected chart: ChartConfiguration<'line'> = USAGE_CHART;

  private filterChanged$: Subject<UsageAggregationFilter> = new ReplaySubject<UsageAggregationFilter>(1);

  constructor(
    private readonly translocoService: TranslocoService,
    private readonly destroyRef: DestroyRef,
    private readonly snackbarService: SnackbarService
  ) {}

  ngOnInit(): void {
    this.filterChanged$
      .pipe(
        switchMap(filter => combineLatest([of(filter), this.loadFn(filter)])),
        takeUntilDestroyed(this.destroyRef),
        catchError(error => {
          const { chart } = this.baseChart;
          if (chart) {
            chart.data.datasets[0].data = [];
            chart.data.labels = undefined;
          }
          this.snackbarService.showErrorSnackbarWithTransloco('usage.report.error', { traceId: error?.error?.traceId });
          return EMPTY;
        })
      )
      .subscribe(([{ frequency }, { result }]) => {
        const dateFormat = DateFormat[UsageAggregationFrequencyDateFormatMap[frequency]] as Intl.DateTimeFormatOptions;
        const { chart } = this.baseChart;
        if (chart) {
          chart.data.datasets[0].data = result.map(agg => agg.totalUsages);
          chart.data.labels = result.map(agg =>
            new Date(agg.id).toLocaleDateString(this.translocoService.getActiveLang(), dateFormat)
          );
        }

        this.baseChart.update();
      });
  }
}
